/**
 * @fileoverview Price-shape derivation for the `/discover` list row's actions
 * column (WEB-674 / DPL-24).
 *
 * The governing rule is short and absolute: **never one number standing in
 * for a range.** A row that shows a single figure for an event whose tickets
 * span a real min–max range has made a promise the checkout page will break.
 * This module is the pure branch logic behind that rule — unit-testable away
 * from the DOM, per the issue's own implementation note ("the branching in a
 * pure helper; five states inside JSX becomes unreadable and untestable").
 *
 * Four render shapes, discriminated by `kind`:
 *
 *   - `unknown` — `priceKnown === false`, OR a `priceMin` the wire never
 *     actually published (`null`). **This branch fires before `isFree` is
 *     ever read**, so a malformed payload carrying `priceKnown: false` and a
 *     legacy fabricated `priceMin: 0` can never render `FREE` — the exact
 *     "$0 read as Free" defect this project exists to delete.
 *   - `free` — a real, published $0 (`event.isFree`, the server-derived
 *     field — never re-derived from `priceMin === 0` here, which would drift
 *     from the wire's own definition during a partial rollout).
 *   - `single` — one published figure (`priceMin === priceMax`).
 *   - `tiered` — a real min–max range. `tierCount` is nullable **by design**:
 *     `null` means "the upstream source never declared a tier cardinality",
 *     and the row must omit the tier line rather than render a fabricated
 *     "1 tier" or infer one from `priceMin !== priceMax`.
 *
 * String formatting (the `PRICE_FREE` / `PRICE_NOT_LISTED` copy, and the
 * `Intl.NumberFormat` currency fallback) intentionally mirrors
 * `lib/copy/priceLabel.ts` — the canonical price-label module every other
 * price-optional surface (map hover card, event-detail at-a-glance) already
 * flows through. This module does not import it: `formatEventPriceLabel`
 * only ever returns ONE string for the whole price, and this row needs the
 * min figure and the full range as two separate strings (`FROM $18` above,
 * `$18-$35 · 3 TIERS` below) — a shape that module was never built to
 * express. The literal `FREE` / `Price not listed` strings are re-exported
 * from `lib/copy/priceLabel.ts` so the two modules cannot drift on copy;
 * import `PRICE_FREE` / `PRICE_NOT_LISTED` from there at the call site.
 *
 * @since WEB-674 (Discovery v2 M3 — DPL-24)
 */

import type { CtaType } from '@loci/types';

/**
 * The subset of `DiscoverEvent` this module needs. Declared locally (not
 * imported from `@loci/types`) so the pure branch logic stays decoupled from
 * the full wire contract — any object with this shape can drive it,
 * including a narrower test fixture.
 */
export interface DiscoverPriceShapeInput {
  readonly priceKnown: boolean;
  readonly priceMin: number | null;
  readonly priceMax: number | null;
  readonly priceTierCount: number | null;
  readonly plusFees: boolean | null;
  readonly isFree: boolean;
  readonly currency: string;
  /**
   * The server-derived CTA affordance. Consumed here ONLY to derive the
   * `free` shape's `condition` — `'rsvp'` is the one real, already-on-the-wire
   * signal that a free event still requires registration rather than walk-up
   * entry. No dedicated "free condition" field exists on `DiscoverEvent`
   * today; inventing one would be exactly the fabrication this project
   * forbids, so the condition is derived from an existing field or omitted.
   */
  readonly ctaType?: CtaType;
}

export interface DiscoverPriceShapeUnknown {
  readonly kind: 'unknown';
}

export interface DiscoverPriceShapeFree {
  readonly kind: 'free';
  /** `'RSVP required'` when `ctaType === 'rsvp'`; otherwise `null` (no condition to state). */
  readonly condition: string | null;
  readonly showFees: boolean;
}

export interface DiscoverPriceShapeSingle {
  readonly kind: 'single';
  /** The 18px display figure, e.g. `"$25"`. */
  readonly figure: string;
  readonly showFees: boolean;
}

export interface DiscoverPriceShapeTiered {
  readonly kind: 'tiered';
  /** The 18px display figure — the lowest published price, e.g. `"$18"`. */
  readonly figure: string;
  /** The full min-max range, e.g. `"$18-$35"`. */
  readonly range: string;
  /** `null` when the upstream source never declared a tier count — omit the tier line, never infer or fabricate one. */
  readonly tierCount: number | null;
  readonly showFees: boolean;
}

export type DiscoverPriceShape =
  | DiscoverPriceShapeUnknown
  | DiscoverPriceShapeFree
  | DiscoverPriceShapeSingle
  | DiscoverPriceShapeTiered;

/**
 * Format a single amount as whole-currency (no fractional digits), matching
 * `lib/copy/priceLabel.ts`'s fallback behavior: an `Intl.NumberFormat`
 * construction failure (an unrecognised currency code from a malformed feed
 * row) falls back to a plain `$`-prefixed literal rather than throwing.
 */
function formatFigure(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `$${amount}`;
  }
}

/**
 * Resolve an event's honest price shape for the discover row actions column.
 *
 * Branch order is load-bearing — read top to bottom:
 *
 *   1. `!priceKnown || priceMin === null` → `unknown`. This MUST be checked
 *      before `isFree`: a `priceKnown: false` payload with a legacy
 *      fabricated `priceMin: 0` must never fall through to the `free`
 *      branch just because `0` looks free.
 *   2. `isFree` → `free`, reading the server-derived flag directly.
 *   3. a real range (`priceMax > priceMin`) → `tiered`.
 *   4. else → `single`.
 */
export function resolveDiscoverPriceShape(input: DiscoverPriceShapeInput): DiscoverPriceShape {
  if (!input.priceKnown || input.priceMin === null) {
    return { kind: 'unknown' };
  }

  const showFees = input.plusFees === true;

  if (input.isFree) {
    return {
      kind: 'free',
      condition: input.ctaType === 'rsvp' ? 'RSVP required' : null,
      showFees,
    };
  }

  const priceMin = input.priceMin;
  const priceMax = input.priceMax ?? priceMin;

  if (priceMax > priceMin) {
    return {
      kind: 'tiered',
      figure: formatFigure(priceMin, input.currency),
      range: `${formatFigure(priceMin, input.currency)}-${formatFigure(priceMax, input.currency)}`,
      tierCount: input.priceTierCount,
      showFees,
    };
  }

  return {
    kind: 'single',
    figure: formatFigure(priceMin, input.currency),
    showFees,
  };
}
